/**
 * 流程變量過濾器組件
 * 包含兩個層級的分組設置：
 *   1. 流程級 - 流程資料格 (Process Variable)
 *   2. 表單級 - eForm 欄位（從實際流程實例的 fieldDisplaySettings 取得）
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Empty, Spin, message, Form, Row, Col, Select, Input, Button,
  Checkbox, Divider, Typography, Tag, Collapse, Tooltip
} from 'antd';
import {
  PlusOutlined, AppstoreOutlined, FormOutlined,
  LoadingOutlined, ClearOutlined, CloseOutlined
} from '@ant-design/icons';
import { useLanguage } from '../../../../contexts/LanguageContext';

const { Text } = Typography;

// ─── API helpers ──────────────────────────────────────────────

const getToken = () => localStorage.getItem('token');

const fetchProcessVariableDefinitions = async (workflowDefinitionId) => {
  try {
    if (!workflowDefinitionId) return [];
    const response = await fetch(
      `/api/processvariables/definitions?workflowDefinitionId=${workflowDefinitionId}`,
      { headers: { 'Authorization': `Bearer ${getToken()}` } }
    );
    if (!response.ok) return response.status === 404 ? [] : (() => { throw new Error(`HTTP ${response.status}`); })();
    const result = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('獲取流程變量定義失敗:', error);
    throw error;
  }
};

const fetchWorkflowDefinitionId = async (workflowName) => {
  try {
    if (!workflowName || !getToken()) return null;
    const response = await fetch('/api/workflowdefinitions', {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) return null;
    const result = await response.json();
    const definitions = Array.isArray(result) ? result : (result.data || []);
    const found = definitions.find(d => d.name === workflowName);
    return found ? found.id : null;
  } catch {
    return null;
  }
};

/**
 * 從工作流定義的 sendEForm 節點取得 eForm 欄位顯示設定
 * 步驟：
 *   1. 取工作流定義 ID
 *   2. 讀取流程定義 JSON，找出所有 sendEForm 節點的 formId
 *   3. 對每個 formId 呼叫 GET /api/eforms/{formId}，讀取定義上的 fieldDisplaySettings
 *   4. 只保留 showInList: true 的欄位
 * 回傳 Map: { formName → { fieldId → displayLabel } }
 */
const fetchWorkflowEformFields = async (workflowName) => {
  try {
    if (!workflowName || !getToken()) return {};

    // 1. 取工作流定義 ID
    const workflowDefinitionId = await fetchWorkflowDefinitionId(workflowName);
    if (!workflowDefinitionId) return {};

    // 2. 取完整工作流定義（含 json 欄位）
    const defResp = await fetch(`/api/workflowdefinitions/${workflowDefinitionId}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!defResp.ok) return {};
    const workflowDef = await defResp.json();

    // 3. 解析流程 JSON，找出所有 sendEForm 節點
    let flowData;
    try {
      flowData = typeof workflowDef.json === 'string'
        ? JSON.parse(workflowDef.json)
        : workflowDef.json;
    } catch {
      console.warn('[fetchWorkflowEformFields] 解析流程 JSON 失敗');
      return {};
    }

    const nodes = flowData?.nodes || [];
    const sendEFormNodes = nodes.filter(n => n.data?.type === 'sendEForm' && n.data?.formId);
    const uniqueFormIds = [...new Set(sendEFormNodes.map(n => n.data.formId))];
    if (uniqueFormIds.length === 0) return {};

    // 4. 並行讀取每個 eForm 定義（含 fieldDisplaySettings）
    const eformDefs = await Promise.all(
      uniqueFormIds.map(formId =>
        fetch(`/api/eforms/${formId}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }).then(r => r.ok ? r.json() : null).catch(() => null)
      )
    );

    // 5. 建立 formName → { fieldId: displayLabel } 對映（只含 showInList: true 欄位）
    const formFieldsMap = {};
    eformDefs.filter(Boolean).forEach(eform => {
      const formName = eform.name || eform.Name;
      if (!formName) return;

      let settings = eform.fieldDisplaySettings || eform.field_display_settings;
      if (typeof settings === 'string') {
        try { settings = JSON.parse(settings); } catch { settings = null; }
      }
      if (!Array.isArray(settings) || settings.length === 0) return;

      const displayFields = {};
      settings
        .filter(f => f.showInList && f.fieldId)
        .forEach(f => {
          displayFields[f.fieldId] = f.displayLabel || f.originalLabel || f.fieldId;
        });

      if (Object.keys(displayFields).length > 0) {
        formFieldsMap[formName] = displayFields;
      }
    });

    return formFieldsMap;
  } catch (error) {
    console.error('獲取工作流 eForm 欄位失敗:', error);
    return {};
  }
};

// ─── 視覺區塊樣式 ─────────────────────────────────────────────

const PV_SECTION_STYLE = {
  borderLeft: '3px solid #1890ff',
  paddingLeft: '12px',
};

const EFORM_SECTION_STYLE = {
  borderLeft: '3px solid #52c41a',
  paddingLeft: '12px',
};

// ─── 元件 ─────────────────────────────────────────────────────

const ProcessVariableFilters = ({
  selectedWorkflowName,
  selectedPvFilters = [],
  onPvFiltersChange,
  selectedGroupingPvs = [],
  onGroupingPvsChange,
  onProcessVariablesLoaded,
  // eForm 分組相關 — 格式: [{ eformName, fieldId, displayLabel }]
  selectedGroupingEformFields = [],
  onGroupingEformFieldsChange,
}) => {
  const { t } = useLanguage();
  const [form] = Form.useForm();

  // ── PV 狀態 ──
  const [processVariables, setProcessVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroupingVariables, setSelectedGroupingVariables] = useState(selectedGroupingPvs || []);

  // ── eForm 狀態 ──
  const [eformFormFieldsMap, setEformFormFieldsMap] = useState({});  // { formName: { fieldId: displayLabel } }
  const [loadingEforms, setLoadingEforms] = useState(false);
  const [expandedEformKeys, setExpandedEformKeys] = useState([]);

  // ── 載入流程資料格 ──
  useEffect(() => {
    const load = async () => {
      if (!selectedWorkflowName) {
        setProcessVariables([]);
        form.resetFields();
        if (onProcessVariablesLoaded) onProcessVariablesLoaded([]);
        return;
      }
      setLoading(true);
      try {
        const defId = await fetchWorkflowDefinitionId(selectedWorkflowName);
        if (!defId) {
          setProcessVariables([]);
          if (onProcessVariablesLoaded) onProcessVariablesLoaded([]);
          return;
        }
        const pvDefs = await fetchProcessVariableDefinitions(defId);
        setProcessVariables(pvDefs);
        if (onProcessVariablesLoaded) onProcessVariablesLoaded(pvDefs);
      } catch {
        message.error('載入流程資料格定義失敗');
        setProcessVariables([]);
        if (onProcessVariablesLoaded) onProcessVariablesLoaded([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkflowName]);

  // ── 載入 eForm 欄位（從實際實例的 fieldDisplaySettings） ──
  useEffect(() => {
    if (!selectedWorkflowName) {
      setEformFormFieldsMap({});
      return;
    }
    let cancelled = false;
    setLoadingEforms(true);
    fetchWorkflowEformFields(selectedWorkflowName)
      .then(map => { if (!cancelled) setEformFormFieldsMap(map); })
      .catch(() => { if (!cancelled) setEformFormFieldsMap({}); })
      .finally(() => { if (!cancelled) setLoadingEforms(false); });
    return () => { cancelled = true; };
  }, [selectedWorkflowName]);

  // ── 同步外部 selectedGroupingPvs ──
  useEffect(() => { setSelectedGroupingVariables(selectedGroupingPvs || []); }, [selectedGroupingPvs]);

  // ── PV 分組 ──
  const handleGroupingVariablesChange = (checkedValues) => {
    setSelectedGroupingVariables(checkedValues);
    if (onGroupingPvsChange) onGroupingPvsChange(checkedValues);
  };

  // ── eForm 欄位分組 ──
  const handleEformFieldGroupingChange = useCallback((eformName, checkedFieldIds) => {
    const fieldsMap = eformFormFieldsMap[eformName] || {};
    const otherEformFields = selectedGroupingEformFields.filter(f => f.eformName !== eformName);
    const newFields = checkedFieldIds.map(fieldId => ({
      eformName,
      fieldId,
      displayLabel: fieldsMap[fieldId] || fieldId,
    }));
    if (onGroupingEformFieldsChange) onGroupingEformFieldsChange([...otherEformFields, ...newFields]);
  }, [eformFormFieldsMap, selectedGroupingEformFields, onGroupingEformFieldsChange]);

  // 移除單一 eForm 欄位
  const handleRemoveEformField = useCallback((eformName, fieldId) => {
    if (onGroupingEformFieldsChange) {
      onGroupingEformFieldsChange(
        selectedGroupingEformFields.filter(f => !(f.eformName === eformName && f.fieldId === fieldId))
      );
    }
  }, [selectedGroupingEformFields, onGroupingEformFieldsChange]);

  // 清除所有 eForm 分組
  const handleClearAllEformFields = useCallback(() => {
    if (onGroupingEformFieldsChange) onGroupingEformFieldsChange([]);
  }, [onGroupingEformFieldsChange]);

  const getSelectedFieldIdsForEform = (eformName) =>
    selectedGroupingEformFields.filter(f => f.eformName === eformName).map(f => f.fieldId);

  // ── PV 過濾條件表單 ──
  const handleFormChange = () => {
    try {
      const values = form.getFieldsValue();
      const conditions = (values.conditions || [])
        .filter(c => c && c.variableName && c.operator &&
          c.value !== undefined && c.value !== null && c.value !== '')
        .map(c => ({ variableName: c.variableName, operator: c.operator, value: String(c.value) }));
      if (onPvFiltersChange) onPvFiltersChange(conditions);
    } catch {
      if (onPvFiltersChange) onPvFiltersChange([]);
    }
  };

  useEffect(() => {
    if (selectedPvFilters && selectedPvFilters.length > 0) {
      form.setFieldsValue({
        conditions: selectedPvFilters.map(f => ({
          variableName: f.variableName, operator: f.operator || 'equals', value: f.value || ''
        }))
      });
    } else {
      const cur = form.getFieldsValue();
      if (!cur.conditions || cur.conditions.length === 0) form.setFieldsValue({ conditions: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPvFilters]);

  if (!selectedWorkflowName) {
    return (
      <Empty
        description={<span>{t('reports.selectWorkflowFirst') || '請先選擇工作流名稱以載入流程資料格'}</span>}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const totalSelectedEformFields = selectedGroupingEformFields.length;
  const eformFormNames = Object.keys(eformFormFieldsMap).sort();

  return (
    <Spin spinning={loading}>
      {/* ══════════════════════════════════════════════════
          流程級：流程資料格 分組
      ══════════════════════════════════════════════════ */}
      <div style={PV_SECTION_STYLE}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <AppstoreOutlined style={{ color: '#1890ff' }} />
          <Tag color="blue" style={{ margin: 0 }}>流程級</Tag>
          <Text strong style={{ fontSize: '14px' }}>流程資料格 分組</Text>
        </div>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          {t('reports.groupingVariablesHelp') || '選擇的流程資料格將用於對媒體文件進行分組，相同值的流程實例會合併到同一組'}
        </Text>

        {(() => {
          const displayablePvs = processVariables.filter(pv => pv.showInList);
          if (processVariables.length === 0 && !loading) {
            return <Text type="secondary" style={{ fontSize: '13px' }}>（此工作流沒有定義流程資料格）</Text>;
          }
          if (displayablePvs.length === 0 && !loading) {
            return (
              <Text type="secondary" style={{ fontSize: '13px' }}>
                （此工作流的流程資料格均未設定「顯示」，請在流程設計器中開啟 showInList）
              </Text>
            );
          }
          return (
            <Checkbox.Group
              value={selectedGroupingVariables}
              onChange={handleGroupingVariablesChange}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 16px' }}>
                {displayablePvs.map(pv => (
                  <Checkbox key={pv.id} value={pv.variableName} style={{ marginRight: 0, marginBottom: 0 }}>
                    <span style={{ fontWeight: 500 }}>{pv.displayName || pv.variableName}</span>
                    {pv.dataType && (
                      <span style={{ color: '#999', marginLeft: '4px', fontSize: '12px' }}>({pv.dataType})</span>
                    )}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          );
        })()}

        {selectedGroupingVariables.length > 0 && (
          <div style={{
            marginTop: '10px', padding: '6px 10px',
            backgroundColor: '#e6f4ff', borderRadius: '4px', fontSize: '13px', color: '#0050b3'
          }}>
            <strong>已選：</strong>
            {selectedGroupingVariables.map(varName => {
              const pv = processVariables.find(p => p.variableName === varName);
              return pv ? (pv.displayName || pv.variableName) : varName;
            }).join('、')}
          </div>
        )}
      </div>

      <Divider style={{ margin: '14px 0' }} />

      {/* ══════════════════════════════════════════════════
          表單級：eForm 欄位 分組
      ══════════════════════════════════════════════════ */}
      <div style={EFORM_SECTION_STYLE}>
        {/* 標題列 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <FormOutlined style={{ color: '#52c41a' }} />
          <Tag color="green" style={{ margin: 0 }}>表單級</Tag>
          <Text strong style={{ fontSize: '14px' }}>表單欄位 分組</Text>
          {totalSelectedEformFields > 0 && (
            <Tag color="green" style={{ borderRadius: '10px', fontSize: '12px' }}>
              已選 {totalSelectedEformFields} 欄
            </Tag>
          )}
          {totalSelectedEformFields > 0 && (
            <Tooltip title="清除所有表單欄位分組">
              <Button
                type="text" size="small" danger
                icon={<ClearOutlined />}
                onClick={handleClearAllEformFields}
              >
                清除全部
              </Button>
            </Tooltip>
          )}
        </div>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          從此工作流的實際表單實例中，選擇設定了「顯示」的欄位用於分組。一個流程可有多個表單定義，每個定義下可有多筆實例。
        </Text>

        {loadingEforms ? (
          <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LoadingOutlined style={{ color: '#52c41a' }} />
            <Text type="secondary" style={{ fontSize: '13px' }}>正在讀取此工作流的表單實例...</Text>
          </div>
        ) : eformFormNames.length === 0 ? (
          <Empty
            description={
              <span style={{ fontSize: '13px' }}>
                此工作流的近期實例中未找到含有「顯示欄位」設定的表單
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Collapse
            ghost
            activeKey={expandedEformKeys}
            onChange={setExpandedEformKeys}
            style={{ background: 'transparent' }}
            items={eformFormNames.map(formName => {
              const fieldsForForm = eformFormFieldsMap[formName] || {};
              const fieldEntries = Object.entries(fieldsForForm); // [[fieldId, displayLabel], ...]
              const selectedForThisEform = getSelectedFieldIdsForEform(formName);
              const selCount = selectedForThisEform.length;

              return {
                key: formName,
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: selCount > 0 ? 600 : 400 }}>{formName}</span>
                    {selCount > 0 && (
                      <Tag color="green" style={{ fontSize: '11px', borderRadius: '8px' }}>
                        {selCount}/{fieldEntries.length} 已選
                      </Tag>
                    )}
                  </div>
                ),
                style: {
                  marginBottom: '4px',
                  border: selCount > 0 ? '1px solid #b7eb8f' : '1px solid #e8e8e8',
                  borderRadius: '6px',
                  background: selCount > 0 ? '#f6ffed' : '#fafafa',
                },
                children: (
                  fieldEntries.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: '12px' }}>此表單沒有設定為「顯示」的欄位</Text>
                  ) : (
                    <Checkbox.Group
                      value={selectedForThisEform}
                      onChange={(checked) => handleEformFieldGroupingChange(formName, checked)}
                      style={{ width: '100%' }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px' }}>
                        {fieldEntries.map(([fieldId, displayLabel]) => (
                          <Checkbox key={fieldId} value={fieldId} style={{ marginRight: 0 }}>
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>{displayLabel}</span>
                            <span style={{ color: '#bbb', marginLeft: '4px', fontSize: '11px' }}>
                              ({fieldId})
                            </span>
                          </Checkbox>
                        ))}
                      </div>
                    </Checkbox.Group>
                  )
                ),
              };
            })}
          />
        )}

        {/* 已選摘要 + 逐項移除 */}
        {totalSelectedEformFields > 0 && (
          <div style={{
            marginTop: '10px', padding: '8px 12px',
            backgroundColor: '#f6ffed', border: '1px solid #b7eb8f',
            borderRadius: '4px', fontSize: '13px', color: '#135200'
          }}>
            <Text strong style={{ color: '#135200', marginRight: '8px' }}>
              已選中用於分組（表單欄位）：
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
              {selectedGroupingEformFields.map(({ eformName, fieldId, displayLabel }) => (
                <Tag
                  key={`${eformName}:${fieldId}`}
                  color="green"
                  closable
                  onClose={() => handleRemoveEformField(eformName, fieldId)}
                  closeIcon={<CloseOutlined style={{ fontSize: '10px' }} />}
                  style={{ fontSize: '12px' }}
                >
                  {eformName} › {displayLabel || fieldId}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>

      <Divider style={{ margin: '14px 0' }} />

      {/* ══════════════════════════════════════════════════
          流程資料格 過濾條件
      ══════════════════════════════════════════════════ */}
      <div>
        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '6px' }}>
          {t('reports.filterConditions') || '流程資料格過濾條件'}
        </Text>
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '12px' }}>
          {t('reports.filterConditionsHelp') || '篩選符合條件的流程實例（僅針對流程資料格）'}
        </Text>
      </div>

      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <Form.List name="conditions">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  style={{
                    border: '1px solid #d9d9d9', borderRadius: '6px',
                    padding: '12px 12px 0', marginBottom: '10px',
                    background: '#fafafa', position: 'relative'
                  }}
                >
                  <Button
                    type="text" danger size="small"
                    style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}
                    onClick={() => { remove(name); setTimeout(() => handleFormChange(), 0); }}
                  >
                    {t('reports.removeFilter') || '移除'}
                  </Button>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        {...restField} name={[name, 'variableName']}
                        label={<span>* {t('reports.columnName') || '欄位名稱'}</span>}
                        rules={[{ required: true, message: '請選擇欄位名稱' }]}
                      >
                        <Select placeholder="選擇欄位">
                          {processVariables.map(pv => (
                            <Select.Option key={pv.id} value={pv.variableName}>
                              {pv.displayName || pv.variableName}
                              {pv.dataType && ` (${pv.dataType})`}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField} name={[name, 'operator']}
                        label={<span>* {t('reports.operator') || '運算子'}</span>}
                        rules={[{ required: true, message: '請選擇運算子' }]}
                      >
                        <Select placeholder="選擇運算子">
                          <Select.Option value="equals">Equals</Select.Option>
                          <Select.Option value="contains">Contains</Select.Option>
                          <Select.Option value="greater_than">Greater Than</Select.Option>
                          <Select.Option value="less_than">Less Than</Select.Option>
                          <Select.Option value="not_equals">Not Equals</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    {...restField} name={[name, 'value']}
                    label={<span>* {t('reports.value') || '值'}</span>}
                    rules={[{ required: true, message: '請輸入搜尋值' }]}
                  >
                    <Input placeholder="輸入搜尋值" />
                  </Form.Item>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ variableName: undefined, operator: undefined, value: undefined })}
                block icon={<PlusOutlined />}
              >
                {t('reports.addSearchCondition') || '新增過濾條件'}
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Spin>
  );
};

export default ProcessVariableFilters;
